import React, { useEffect, useState, useContext } from "react";
import { statusApi, handleApiError } from "../../utils/api";
import { AuthContext } from "../../contexts/AuthContext";

const CreateStatusForm = ({ onCreated }) => {
  const [formData, setFormData] = useState({
    status: "",
    status_description: "",
    status_type_id: "",
  });

  const [statusTypes, setStatusTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { user, isAuthenticated } = useContext(AuthContext);

  useEffect(() => {
    if (user) {
      fetchStatusTypes();
    }
  }, [user]);

  const fetchStatusTypes = async () => {
    try {
      // HIPAA Compliant: Using statusApi with secure httpOnly cookie auth
      const response = await statusApi.getTypes();
      setStatusTypes(response.data);
    } catch (err) {
      console.error("❌ Failed to load status types", err);
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!formData.status.trim()) {
      setError("Status name is required");
      return;
    }

    if (!formData.status_type_id) {
      setError("Status type is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // HIPAA Compliant: Using statusApi with secure httpOnly cookie auth
      await statusApi.create({
        status: formData.status.trim(),
        status_description: formData.status_description.trim(),
        status_type_id: formData.status_type_id,
        // created_by is handled server-side from the authenticated user
      });

      // Success notification
      alert("✅ Status created successfully!");

      // Reset form
      setFormData({
        status: "",
        status_description: "",
        status_type_id: "",
      });

      // Notify parent component if callback provided
      if (onCreated) {
        onCreated();
      }
    } catch (err) {
      console.error("❌ Error creating status", err);
      const apiError = handleApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  // Check authentication
  if (!user) {
    return (
      <div className="alert alert-warning" role="alert">
        Please log in to create statuses.
      </div>
    );
  }

  // Check authorization
  if (user?.role !== "admin") {
    return (
      <div className="alert alert-info" role="alert">
        Only administrators can create statuses.
      </div>
    );
  }

  return (
    <div>
      <h4>Create New Status</h4>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3">
        <div className="col-md-6">
          <label htmlFor="status-name" className="form-label">
            Status Name <span className="text-danger">*</span>
          </label>
          <input
            id="status-name"
            className="form-control"
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
            maxLength={100}
            placeholder="Enter status name"
            disabled={loading}
          />
        </div>

        <div className="col-md-6">
          <label htmlFor="status-type" className="form-label">
            Status Type <span className="text-danger">*</span>
          </label>
          <select
            id="status-type"
            className="form-select"
            name="status_type_id"
            value={formData.status_type_id}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Select Type</option>
            {statusTypes.map((type) => (
              <option key={type.status_type_id} value={type.status_type_id}>
                {type.status_type_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-12">
          <label htmlFor="status-description" className="form-label">
            Description
          </label>
          <textarea
            id="status-description"
            className="form-control"
            name="status_description"
            value={formData.status_description}
            onChange={handleChange}
            rows="3"
            maxLength={500}
            placeholder="Enter status description (optional)"
            disabled={loading}
          />
          <div className="form-text">
            {formData.status_description.length}/500 characters
          </div>
        </div>

        <div className="col-12">
          <button
            className="btn btn-success"
            type="submit"
            disabled={
              loading || !formData.status.trim() || !formData.status_type_id
            }
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Creating...
              </>
            ) : (
              "Create Status"
            )}
          </button>

          <button
            type="button"
            className="btn btn-secondary ms-2"
            onClick={() => {
              setFormData({
                status: "",
                status_description: "",
                status_type_id: "",
              });
              setError(null);
            }}
            disabled={loading}
          >
            Clear
          </button>
        </div>
      </form>

      {/* Audit info for HIPAA compliance */}
      <div className="mt-3">
        <small className="text-muted">
          <i className="bi bi-info-circle"></i> All actions are logged for audit
          purposes.
        </small>
      </div>
    </div>
  );
};

export default CreateStatusForm;
